import { useState, useEffect, useCallback } from 'react';
import { MeetingService } from '../services/meetingService';
import { MeetingInstance, MeetingType } from '../types/database';
import { useAuth } from '../contexts/AuthContext';

export function useMeetings() {
  const { user } = useAuth();
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [instances, setInstances] = useState<MeetingInstance[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadMeetingTypes = useCallback(async () => {
    if (!user) return;

    const { data } = await MeetingService.getConvenorMeetingTypes(user.id);
    if (data) {
      setMeetingTypes(data);
    }
  }, [user]);

  const loadInstances = useCallback(async () => {
    if (!user) return;

    const { data } = await MeetingService.getMyInstances(user.id);
    if (data) {
      setInstances(data);
    }
  }, [user]);

  const loadStats = useCallback(async () => {
    if (!user) return;

    const { data } = await MeetingService.getConvenorStats(user.id);
    if (data) {
      setStats(data);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (user) {
      loadMeetingTypes();
      loadInstances();
      loadStats();
    }
  }, [user, loadMeetingTypes, loadInstances, loadStats]);

  const createInstance = async (meetingTypeId: string, formData: any) => {
    if (!user) return { success: false, error: 'User not logged in' };

    const { data, error } = await MeetingService.createInstance(user.id, meetingTypeId, formData);
    if (error) {
      return { success: false, error: error.message };
    }

    await loadInstances();
    await loadStats();
    return { success: true, data };
  };

  const updateInstance = async (instanceId: string, updates: Partial<MeetingInstance>) => {
    const { data, error } = await MeetingService.updateInstance(instanceId, updates);
    if (error) {
      return { success: false, error: error.message };
    }

    await loadInstances();
    await loadStats();
    return { success: true, data };
  };

  const deleteInstance = async (instanceId: string) => {
    const { error } = await MeetingService.deleteInstance(instanceId);
    if (error) {
      return { success: false, error: error.message };
    }

    await loadInstances();
    await loadStats();
    return { success: true };
  };

  const refresh = () => {
    loadMeetingTypes();
    loadInstances();
    loadStats();
  };

  return {
    meetingTypes,
    instances,
    stats,
    loading,
    createInstance,
    updateInstance,
    deleteInstance,
    refresh
  };
}
